/**
 * <DashedButton> — a dashed, block "dropzone" button (atom, L0.1).
 *
 * Named for its look, not a domain: a full-width, tall pressable with a dashed border and a soft
 * tinted fill, holding a horizontally-centered `<Icon>` + `<Text>` row. It's the maison version of
 * the big "add a photo/document" affordance the legacy `MediaPickerButton` rendered via `buttonStyle`
 * (dashed `primary` border + `primary-soft` background). Purely presentational — no picker logic.
 *
 * `highlighted` flips the chrome to the success palette (drag-active drop target). Default `false`
 * preserves today's look for every existing call-site.
 *
 * Composes the `<Icon>` and `<Text>` atoms (never a hand-rolled glyph/label). While `loading`, the
 * row is replaced by a centered spinner. No hex literals: every color is a `SemanticColorKey`.
 */
import React from "react"
import { ActivityIndicator, Pressable, type GestureResponderEvent } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { textInkOnFill } from "../../theme/tokens/colors"
import { Row } from "../containers/Row"
import { Icon, type IconName } from "../icons/Icon"
import { Text } from "../typography/Text"

export type DashedButtonProps = {
    label: string
    /** FontAwesome5 glyph shown before the label — defaults to `photo-video`. */
    iconName?: IconName
    /** Minimum box height in px — defaults to 180 (the big dropzone). Lower it for a compact button. */
    minHeight?: number
    onPress?: (event: GestureResponderEvent) => void
    /** Swap the icon+label for a centered spinner. */
    loading?: boolean
    disabled?: boolean
    /** Drag-active / drop-target chrome (success border + soft fill). Default false. */
    highlighted?: boolean
    testID?: string
    accessibilityLabel?: string
}

export function DashedButton(props: DashedButtonProps) {
    const {
        label,
        iconName = "photo-video",
        minHeight = 180,
        onPress,
        loading = false,
        disabled = false,
        highlighted = false,
        testID,
        accessibilityLabel,
    } = props

    const { theme } = useUnistyles()
    const isBlocked = disabled || loading
    const accent = highlighted ? "success" : "primary"
    // Le libellé et son glyphe écrivent la couleur : le vert plein ne tient pas l'AA sur sa tinte.
    const ink = textInkOnFill(accent)

    return (
        <Pressable
            testID={testID}
            onPress={onPress}
            disabled={isBlocked}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel ?? label}
            accessibilityState={{ disabled: isBlocked }}
            style={({ pressed }) => [
                styles.root(minHeight, disabled, highlighted),
                pressed && !isBlocked ? styles.pressed : undefined,
            ]}
        >
            {loading ? (
                <ActivityIndicator size="small" color={theme.colors[accent]} />
            ) : (
                <Row gap="2" justify="center" align="center">
                    <Icon name={iconName} size={20} color={ink} />
                    <Text size="text-base" weight="bold" color={ink}>
                        {label}
                    </Text>
                </Row>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: (minHeight: number, disabled: boolean, highlighted: boolean) => ({
        width: "100%" as const,
        minHeight,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        paddingVertical: theme.spacing["4"],
        paddingHorizontal: theme.spacing["5"],
        borderWidth: 2,
        borderStyle: "dashed" as const,
        borderColor: highlighted ? theme.colors.success : theme.colors.primary,
        borderRadius: theme.radius.lg,
        backgroundColor: highlighted ? theme.colors.successLight : theme.colors["primary-soft"],
        opacity: disabled ? 0.5 : 1,
    }),
    pressed: { opacity: 0.85 },
}))
